import React from "react";
import { Clock, TrendingUp, Calendar, Users } from "lucide-react";
import DashboardWidget from "@/components/DashboardWidget";
import { Badge } from "@/components/ui/badge";

interface PeakHoursWidgetProps {
  stats: any;
}

export const PeakHoursWidget: React.FC<PeakHoursWidgetProps> = ({ stats }) => {
  // Mock peak hours data (in real implementation, this would come from your analytics)
  const peakHoursData = {
    todayPeak: { hour: "2:00 PM", visitors: 89 },
    weekPeak: { day: "Tuesday", visitors: 342 },
    hourlyData: [
      { hour: "12 AM", visitors: 12, percentage: 8 },
      { hour: "1 AM", visitors: 8, percentage: 5 },
      { hour: "2 AM", visitors: 5, percentage: 3 },
      { hour: "3 AM", visitors: 3, percentage: 2 },
      { hour: "4 AM", visitors: 4, percentage: 3 },
      { hour: "5 AM", visitors: 7, percentage: 5 },
      { hour: "6 AM", visitors: 15, percentage: 10 },
      { hour: "7 AM", visitors: 28, percentage: 19 },
      { hour: "8 AM", visitors: 45, percentage: 30 },
      { hour: "9 AM", visitors: 67, percentage: 45 },
      { hour: "10 AM", visitors: 78, percentage: 52 },
      { hour: "11 AM", visitors: 85, percentage: 57 },
      { hour: "12 PM", visitors: 92, percentage: 62 },
      { hour: "1 PM", visitors: 96, percentage: 65 },
      { hour: "2 PM", visitors: 148, percentage: 100 }, // Peak hour
      { hour: "3 PM", visitors: 134, percentage: 90 },
      { hour: "4 PM", visitors: 118, percentage: 80 },
      { hour: "5 PM", visitors: 95, percentage: 64 },
      { hour: "6 PM", visitors: 72, percentage: 49 },
      { hour: "7 PM", visitors: 58, percentage: 39 },
      { hour: "8 PM", visitors: 45, percentage: 30 },
      { hour: "9 PM", visitors: 32, percentage: 22 },
      { hour: "10 PM", visitors: 22, percentage: 15 },
      { hour: "11 PM", visitors: 16, percentage: 11 },
    ],
    weeklyData: [
      { day: "Monday", visitors: 298, percentage: 87 },
      { day: "Tuesday", visitors: 342, percentage: 100 },
      { day: "Wednesday", visitors: 315, percentage: 92 },
      { day: "Thursday", visitors: 287, percentage: 84 },
      { day: "Friday", visitors: 325, percentage: 95 },
      { day: "Saturday", visitors: 256, percentage: 75 },
      { day: "Sunday", visitors: 198, percentage: 58 },
    ],
  };

  const getBarHeight = (percentage: number) => {
    return Math.max(percentage * 0.8, 4); // Minimum 4% height for visibility
  };

  const getBarColor = (percentage: number) => {
    if (percentage >= 90) return "bg-red-500";
    if (percentage >= 70) return "bg-orange-500";
    if (percentage >= 50) return "bg-yellow-500";
    if (percentage >= 30) return "bg-green-500";
    return "bg-blue-500";
  };

  return (
    <DashboardWidget id="peak-hours" title="Peak Hours Analysis">
      <div className="space-y-6">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border">
            <div className="flex items-center justify-between mb-2">
              <Clock className="h-5 w-5 text-indigo-600" />
              <Badge className="bg-indigo-100 text-indigo-700">Today</Badge>
            </div>
            <h3 className="font-semibold text-gray-900">Peak Hour Today</h3>
            <div className="text-2xl font-bold text-indigo-600">
              {peakHoursData.todayPeak.hour}
            </div>
            <p className="text-sm text-gray-600">
              {peakHoursData.todayPeak.visitors} visitors
            </p>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-4 border">
            <div className="flex items-center justify-between mb-2">
              <Calendar className="h-5 w-5 text-emerald-600" />
              <Badge className="bg-emerald-100 text-emerald-700">
                This Week
              </Badge>
            </div>
            <h3 className="font-semibold text-gray-900">Peak Day</h3>
            <div className="text-2xl font-bold text-emerald-600">
              {peakHoursData.weekPeak.day}
            </div>
            <p className="text-sm text-gray-600">
              {peakHoursData.weekPeak.visitors} visitors
            </p>
          </div>
        </div>

        {/* Hourly Traffic Pattern */}
        <div className="bg-white rounded-lg border p-4">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="h-4 w-4" />
            Hourly Traffic Pattern
          </h4>
          <div className="flex items-end justify-between gap-1 h-32 mb-2">
            {peakHoursData.hourlyData.map((hour) => (
              <div
                key={hour.hour}
                className="flex flex-col items-center flex-1"
              >
                <div
                  className={`w-full rounded-t-sm ${getBarColor(hour.percentage)} transition-all hover:opacity-80`}
                  style={{ height: `${getBarHeight(hour.percentage)}%` }}
                  title={`${hour.hour}: ${hour.visitors} visitors`}
                ></div>
              </div>
            ))}
          </div>
          <div className="flex justify-between text-xs text-gray-500">
            <span>12 AM</span>
            <span>6 AM</span>
            <span>12 PM</span>
            <span>6 PM</span>
            <span>11 PM</span>
          </div>
        </div>

        {/* Weekly Traffic Pattern */}
        <div className="bg-white rounded-lg border p-4">
          <h4 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Users className="h-4 w-4" />
            Weekly Traffic Pattern
          </h4>
          <div className="space-y-2">
            {peakHoursData.weeklyData.map((day) => (
              <div key={day.day} className="flex items-center gap-3">
                <div className="w-16 text-sm text-gray-600 font-medium">
                  {day.day}
                </div>
                <div className="flex-1 bg-gray-200 rounded-full h-4 relative">
                  <div
                    className={`h-4 rounded-full transition-all ${getBarColor(day.percentage)}`}
                    style={{ width: `${day.percentage}%` }}
                  ></div>
                </div>
                <div className="w-12 text-sm font-semibold text-gray-900 text-right">
                  {day.visitors}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Traffic Insights */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-yellow-50 rounded-lg p-3 border-l-4 border-yellow-400">
            <h5 className="font-medium text-yellow-800">Peak Time</h5>
            <p className="text-sm text-yellow-700">
              2-4 PM sees highest traffic
            </p>
          </div>
          <div className="bg-blue-50 rounded-lg p-3 border-l-4 border-blue-400">
            <h5 className="font-medium text-blue-800">Best Days</h5>
            <p className="text-sm text-blue-700">Tue-Fri perform best</p>
          </div>
          <div className="bg-green-50 rounded-lg p-3 border-l-4 border-green-400">
            <h5 className="font-medium text-green-800">Opportunity</h5>
            <p className="text-sm text-green-700">Weekends need attention</p>
          </div>
        </div>
      </div>

      <div className="mt-4 text-sm text-gray-600">
        <p>• Traffic patterns based on last 30 days</p>
        <p>• Peak hours help optimize content scheduling</p>
      </div>
    </DashboardWidget>
  );
};

export default PeakHoursWidget;
